import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, CalendarDays, LogIn, LogOut, X, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { format, parseISO, addDays } from 'date-fns';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';
const AUTO_REFRESH_INTERVAL = 15000;

const ReservationsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { rooms, roomTypes, fetchRooms } = useHotel();
  const [reservations, setReservations] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [editingReservation, setEditingReservation] = useState(null);
  
  const [formData, setFormData] = useState({
    guest_id: '',
    first_name: '',
    last_name: '',
    room_id: '',
    check_in_date: format(new Date(), 'yyyy-MM-dd'),
    check_out_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
    adults: 1,
    children: 0,
    special_requests: '',
    rate_per_night: '',
    discount_percent: 0,
    source: 'direct'
  });
  const [createNewGuest, setCreateNewGuest] = useState(true);

  const fetchData = useCallback(async (showIndicator = true) => {
    if (!user?.hotel_id) return;
    if (showIndicator) setLoading(true);
    try {
      const [resResponse, guestResponse] = await Promise.all([
        axios.get(`${API}/hotels/${user.hotel_id}/reservations`, { headers: getAuthHeaders() }),
        axios.get(`${API}/hotels/${user.hotel_id}/guests`, { headers: getAuthHeaders() })
      ]);
      setReservations(resResponse.data);
      setGuests(guestResponse.data);
      if (fetchRooms) fetchRooms();
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.hotel_id, getAuthHeaders, fetchRooms]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const intervalId = setInterval(() => fetchData(false), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData(false);
  };

  const handleCreateReservation = async (e) => {
    e.preventDefault();
    try {
      let guestId = formData.guest_id;
      
      // If creating new guest, create guest first
      if (createNewGuest && formData.first_name && formData.last_name) {
        const guestResponse = await axios.post(
          `${API}/hotels/${user.hotel_id}/guests`,
          { first_name: formData.first_name, last_name: formData.last_name },
          { headers: getAuthHeaders() }
        );
        guestId = guestResponse.data.id;
        setGuests(prev => [...prev, guestResponse.data]);
        toast.success(`Guest ${formData.first_name} ${formData.last_name} created`);
      }
      
      if (!guestId) {
        toast.error('Please enter guest name or select existing guest');
        return;
      }
      
      const payload = {
        guest_id: guestId,
        room_id: formData.room_id,
        check_in_date: new Date(formData.check_in_date).toISOString(),
        check_out_date: new Date(formData.check_out_date).toISOString(),
        adults: formData.adults,
        children: formData.children,
        special_requests: formData.special_requests,
        rate_per_night: parseFloat(formData.rate_per_night),
        discount_percent: parseFloat(formData.discount_percent) || 0,
        source: formData.source
      };
      
      if (editingReservation) {
        const response = await axios.put(
          `${API}/hotels/${user.hotel_id}/reservations/${editingReservation.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        setReservations(prev => prev.map(r => r.id === editingReservation.id ? response.data : r));
        toast.success('Reservation updated');
      } else {
        const response = await axios.post(
          `${API}/hotels/${user.hotel_id}/reservations`,
          payload,
          { headers: getAuthHeaders() }
        );
        setReservations(prev => [response.data, ...prev]);
        toast.success('Reservation created');
      }
      
      setShowDialog(false);
      resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save reservation'));
    }
  };

  const handleEditReservation = (res) => {
    setEditingReservation(res);
    setCreateNewGuest(false);
    const guest = guests.find(g => g.id === res.guest_id);
    setFormData({
      guest_id: res.guest_id,
      first_name: guest?.first_name || '',
      last_name: guest?.last_name || '',
      room_id: res.room_id,
      check_in_date: res.check_in_date?.slice(0, 10),
      check_out_date: res.check_out_date?.slice(0, 10),
      adults: res.adults || 1,
      children: res.children || 0,
      special_requests: res.special_requests || '',
      rate_per_night: res.rate_per_night?.toString() || '',
      discount_percent: res.discount_percent || 0,
      source: res.source || 'direct'
    });
    setShowDialog(true);
  };

  const handleDeleteReservation = async (resId) => {
    if (!window.confirm('Delete this reservation? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/hotels/${user.hotel_id}/reservations/${resId}`, {
        headers: getAuthHeaders()
      });
      setReservations(prev => prev.filter(r => r.id !== resId));
      toast.success('Reservation deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete reservation'));
    }
  };

  const handleCheckIn = async (resId) => {
    try {
      await axios.post(
        `${API}/hotels/${user.hotel_id}/reservations/${resId}/check-in`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setReservations(prev => prev.map(r => 
        r.id === resId ? { ...r, status: 'checked_in' } : r
      ));
      if (fetchRooms) fetchRooms();
      toast.success('Guest checked in successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Check-in failed'));
    }
  };

  const handleCheckOut = async (resId) => {
    try {
      await axios.post(
        `${API}/hotels/${user.hotel_id}/reservations/${resId}/check-out`,
        {},
        { headers: getAuthHeaders() }
      );
      
      setReservations(prev => prev.map(r => 
        r.id === resId ? { ...r, status: 'checked_out' } : r
      ));
      if (fetchRooms) fetchRooms();
      toast.success('Guest checked out successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Check-out failed'));
    }
  };

  const handleCancel = async (resId) => {
    if (!window.confirm('Are you sure you want to cancel this reservation?')) return;
    
    try {
      await axios.put(
        `${API}/hotels/${user.hotel_id}/reservations/${resId}`,
        { status: 'cancelled' },
        { headers: getAuthHeaders() }
      );
      
      setReservations(prev => prev.map(r => 
        r.id === resId ? { ...r, status: 'cancelled' } : r
      ));
      toast.success('Reservation cancelled');
    } catch (error) {
      toast.error('Failed to cancel reservation');
    }
  };

  const resetForm = () => {
    setEditingReservation(null);
    setCreateNewGuest(true);
    setFormData({
      guest_id: '',
      first_name: '',
      last_name: '',
      room_id: '',
      check_in_date: format(new Date(), 'yyyy-MM-dd'),
      check_out_date: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
      adults: 1,
      children: 0,
      special_requests: '',
      rate_per_night: '',
      discount_percent: 0,
      source: 'direct'
    });
  };

  const getSourceLabel = (source) => {
    const labels = {
      'direct': 'Direct',
      'booking.com': 'Booking.com',
      'expedia': 'Expedia',
      'airbnb': 'Airbnb'
    };
    return labels[source] || source || 'Direct';
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      confirmed: 'bg-blue-100 text-blue-700',
      checked_in: 'bg-emerald-100 text-emerald-700',
      checked_out: 'bg-slate-100 text-slate-700',
      cancelled: 'bg-rose-100 text-rose-700',
      no_show: 'bg-orange-100 text-orange-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getGuestName = (guestId) => {
    const guest = guests.find(g => g.id === guestId);
    return guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown';
  };

  const getRoomNumber = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.number || room?.room_number || 'N/A';
  };

  const getRoomPrice = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    if (room) {
      const roomType = roomTypes.find(rt => rt.id === room.room_type_id);
      return roomType?.base_rate || roomType?.base_price || 0;
    }
    return 0;
  };

  const availableRooms = rooms.filter(r => r.status === 'available' || r.status === 'clean');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="reservations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-['Outfit']">Reservations</h1>
          <p className="text-sm text-muted-foreground">Manage bookings and check-in/out</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-reservation-btn" disabled={availableRooms.length === 0} size="sm" className="sm:size-default">
                <Plus className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">New Reservation</span>
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto mx-2 sm:mx-auto">
            <DialogHeader>
              <DialogTitle>{editingReservation ? 'Edit Reservation' : 'Create New Reservation'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateReservation} className="space-y-4">
              {/* Guest Section */}
              {!editingReservation && (
                <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg">
                  <Label className="text-sm">Guest:</Label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={createNewGuest ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCreateNewGuest(true)}
                    >
                      New Guest
                    </Button>
                    <Button
                      type="button"
                      variant={!createNewGuest ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCreateNewGuest(false)}
                    >
                      Existing Guest
                    </Button>
                  </div>
                </div>
              )}

              {createNewGuest && !editingReservation ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="John"
                      required
                      data-testid="reservation-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Smith"
                      required
                      data-testid="reservation-last-name-input"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Guest *</Label>
                  <Select
                    value={formData.guest_id}
                    onValueChange={(value) => setFormData({ ...formData, guest_id: value })}
                  >
                    <SelectTrigger data-testid="reservation-guest-select">
                      <SelectValue placeholder="Select guest" />
                    </SelectTrigger>
                    <SelectContent>
                      {guests.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          {g.first_name} {g.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Room *</Label>
                <Select
                  value={formData.room_id}
                  onValueChange={(value) => {
                    const price = getRoomPrice(value);
                    setFormData({ ...formData, room_id: value, rate_per_night: price.toString() });
                  }}
                >
                  <SelectTrigger data-testid="reservation-room-select">
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRooms.map((r) => {
                      const rt = roomTypes.find(t => t.id === r.room_type_id);
                      return (
                        <SelectItem key={r.id} value={r.id}>
                          Room {r.number} - {rt?.name} (€{rt?.base_rate || rt?.base_price || 0})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Check-in Date *</Label>
                  <Input
                    type="date"
                    value={formData.check_in_date}
                    onChange={(e) => setFormData({ ...formData, check_in_date: e.target.value })}
                    data-testid="reservation-checkin-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Check-out Date *</Label>
                  <Input
                    type="date"
                    value={formData.check_out_date}
                    onChange={(e) => setFormData({ ...formData, check_out_date: e.target.value })}
                    data-testid="reservation-checkout-input"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Adults</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.adults}
                    onChange={(e) => setFormData({ ...formData, adults: parseInt(e.target.value) })}
                    data-testid="reservation-adults-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.children}
                    onChange={(e) => setFormData({ ...formData, children: parseInt(e.target.value) })}
                    data-testid="reservation-children-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rate per Night (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.rate_per_night}
                    onChange={(e) => setFormData({ ...formData, rate_per_night: e.target.value })}
                    data-testid="reservation-rate-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Discount %</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                    data-testid="reservation-discount-input"
                  />
                </div>
              </div>

              {/* Total Rate Summary */}
              {(() => {
                const nights = formData.check_in_date && formData.check_out_date
                  ? Math.max(1, Math.round((new Date(formData.check_out_date) - new Date(formData.check_in_date)) / 86400000))
                  : 0;
                const rate = parseFloat(formData.rate_per_night) || 0;
                const discount = parseFloat(formData.discount_percent) || 0;
                const subtotal = rate * nights;
                const discountAmt = subtotal * (discount / 100);
                const total = subtotal - discountAmt;
                return rate > 0 && nights > 0 ? (
                  <div className="rounded-lg border bg-muted/40 p-3 space-y-1.5" data-testid="reservation-total-summary">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>€{rate.toFixed(2)} × {nights} night{nights !== 1 ? 's' : ''}</span>
                      <span>€{subtotal.toFixed(2)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm text-emerald-600">
                        <span>Discount ({discount}%)</span>
                        <span>−€{discountAmt.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-semibold text-base border-t pt-1.5">
                      <span>Total Rate (€)</span>
                      <span data-testid="reservation-total-amount">€{total.toFixed(2)}</span>
                    </div>
                  </div>
                ) : null;
              })()}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
                    <SelectTrigger data-testid="reservation-source-select">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct</SelectItem>
                      <SelectItem value="booking.com">Booking.com</SelectItem>
                      <SelectItem value="expedia">Expedia</SelectItem>
                      <SelectItem value="airbnb">Airbnb</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Special Requests</Label>
                  <Input
                    value={formData.special_requests}
                    onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                    placeholder="Late check-in, extra pillows, etc."
                    data-testid="reservation-requests-input"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full" data-testid="submit-reservation-btn">
                {editingReservation ? 'Update Reservation' : 'Create Reservation'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {guests.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">Add guests first before creating reservations.</p>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All ({reservations.length})</TabsTrigger>
          <TabsTrigger value="today">Today's Arrivals</TabsTrigger>
          <TabsTrigger value="checked_in">In-House</TabsTrigger>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Guest</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Room</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Check-in</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Check-out</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Source</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reservations.map((res) => (
                      <tr key={res.id} className="border-b hover:bg-muted/30" data-testid={`reservation-row-${res.id}`}>
                        <td className="py-3 px-4 font-medium">{getGuestName(res.guest_id)}</td>
                        <td className="py-3 px-4">{getRoomNumber(res.room_id)}</td>
                        <td className="py-3 px-4">{res.check_in_date?.slice(0, 10)}</td>
                        <td className="py-3 px-4">{res.check_out_date?.slice(0, 10)}</td>
                        <td className="py-3 px-4 font-medium">€{(
                      res.total_amount > 0
                        ? res.total_amount
                        : (() => {
                            try {
                              const ci = new Date(res.check_in_date);
                              const co = new Date(res.check_out_date);
                              const nights = Math.max(1, Math.round((co - ci) / 86400000));
                              return (res.rate_per_night || 0) * nights;
                            } catch { return 0; }
                          })()
                    ).toFixed(2)}</td>
                        <td className="py-3 px-4">
                          <Badge variant="outline" className="text-xs">
                            {getSourceLabel(res.source)}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={getStatusBadge(res.status)}>
                            {res.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            {['pending', 'confirmed'].includes(res.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCheckIn(res.id)}
                                data-testid={`checkin-btn-${res.id}`}
                              >
                                <LogIn className="h-4 w-4 mr-1" />
                                Check-in
                              </Button>
                            )}
                            {res.status === 'checked_in' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCheckOut(res.id)}
                                data-testid={`checkout-btn-${res.id}`}
                              >
                                <LogOut className="h-4 w-4 mr-1" />
                                Check-out
                              </Button>
                            )}
                            {!['cancelled', 'checked_out'].includes(res.status) && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditReservation(res)}
                                  data-testid={`edit-reservation-${res.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleCancel(res.id)}
                                  data-testid={`cancel-btn-${res.id}`}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteReservation(res.id)}
                              data-testid={`delete-reservation-${res.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {reservations.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No reservations yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="today">
          <Card>
            <CardHeader>
              <CardTitle>Today's Arrivals</CardTitle>
            </CardHeader>
            <CardContent>
              {reservations
                .filter(r => r.check_in_date?.slice(0, 10) === format(new Date(), 'yyyy-MM-dd') && ['pending', 'confirmed'].includes(r.status))
                .map((res) => (
                  <div key={res.id} className="flex items-center justify-between p-4 border rounded-lg mb-2">
                    <div>
                      <p className="font-medium">{getGuestName(res.guest_id)}</p>
                      <p className="text-sm text-muted-foreground">Room {getRoomNumber(res.room_id)}</p>
                    </div>
                    <Button onClick={() => handleCheckIn(res.id)} data-testid={`today-checkin-${res.id}`}>
                      <LogIn className="h-4 w-4 mr-2" />
                      Check-in
                    </Button>
                  </div>
                ))}
              {reservations.filter(r => r.check_in_date?.slice(0, 10) === format(new Date(), 'yyyy-MM-dd') && ['pending', 'confirmed'].includes(r.status)).length === 0 && (
                <p className="text-center text-muted-foreground py-8">No arrivals today.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="checked_in">
          <Card>
            <CardHeader>
              <CardTitle>In-House Guests</CardTitle>
            </CardHeader>
            <CardContent>
              {reservations
                .filter(r => r.status === 'checked_in')
                .map((res) => (
                  <div key={res.id} className="flex items-center justify-between p-4 border rounded-lg mb-2">
                    <div>
                      <p className="font-medium">{getGuestName(res.guest_id)}</p>
                      <p className="text-sm text-muted-foreground">Room {getRoomNumber(res.room_id)} • Checkout: {res.check_out_date?.slice(0, 10)}</p>
                    </div>
                    <Button variant="outline" onClick={() => handleCheckOut(res.id)} data-testid={`inhouse-checkout-${res.id}`}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Check-out
                    </Button>
                  </div>
                ))}
              {reservations.filter(r => r.status === 'checked_in').length === 0 && (
                <p className="text-center text-muted-foreground py-8">No in-house guests.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calendar">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarDays className="h-5 w-5" />
                  Select Date
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-md border"
                  data-testid="reservation-calendar"
                />
              </CardContent>
            </Card>
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>
                  Reservations for {format(selectedDate || new Date(), 'MMMM d, yyyy')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reservations
                  .filter(r => {
                    const date = format(selectedDate || new Date(), 'yyyy-MM-dd');
                    return r.check_in_date?.slice(0, 10) <= date && r.check_out_date?.slice(0, 10) > date;
                  })
                  .map((res) => (
                    <div key={res.id} className="flex items-center justify-between p-4 border rounded-lg mb-2">
                      <div>
                        <p className="font-medium">{getGuestName(res.guest_id)}</p>
                        <p className="text-sm text-muted-foreground">Room {getRoomNumber(res.room_id)}</p>
                      </div>
                      <Badge className={getStatusBadge(res.status)}>{res.status}</Badge>
                    </div>
                  ))}
                {reservations.filter(r => {
                  const date = format(selectedDate || new Date(), 'yyyy-MM-dd');
                  return r.check_in_date?.slice(0, 10) <= date && r.check_out_date?.slice(0, 10) > date;
                }).length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No reservations for this date.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReservationsPage;
